import { FC, ReactNode, useMemo, useCallback } from 'react'
import { FutureInputDecoration } from 'components/features/future/FutureInputDecoration/FutureInputDecoration'
import { FutureInputWithControl } from 'components/features/future/FutureInputDecoration/FutureInputWithControl'
import { FutureFieldControlButtons } from 'components/features/future/FutureFieldControl/FutureFieldControlButtons'
import { useFormContext } from 'react-hook-form'
import { MaskOptions, MaskedInputController, MaskedInputProps } from 'components/atoms/inputs/maskedInput/MaskedInput/MaskedInput'
import { FutureInput } from 'components/atoms/inputs/FutureInput/FutureInput'
import { PremiumInput } from 'components/features/future/FutureField/PremiumInput'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { isMemberPremiumSelector } from 'selectors/globalSelectors'
import IMask from 'imask'
import { ThemeProvider } from 'components/features/theme/ThemeProvider/ThemeProvider'

type RangeProps = {
  min: number
  max: number
  step: number
}

type FutureFieldButtonsProps =
  & RangeProps
  & {
    mask: MaskOptions
    name: string
    label: string
    error?: string
    units?: string
    note?: ReactNode
    inputMode?: MaskedInputProps['inputMode']
    isPremium?: boolean
    isDisabled?: boolean
    onFocus?: () => void
  }
export const FutureFieldButtons: FC<FutureFieldButtonsProps> = (props) => {
  const { label, error, note, name, units, min, max, step, mask, isPremium, isDisabled, inputMode, onFocus } = props

  const isMemberPremium = useStoreSelector(isMemberPremiumSelector)
  const isPremiumRequired = useMemo(() => !!(isPremium && !isMemberPremium), [isMemberPremium, isPremium])

  const { getValues, setValue, watch } = useFormContext<Record<string, string>>()

  const handleIncrement = useCallback(() => {
    const fieldValue = parseFloat(getValues(name))
    const nextValue = Math.min(max, (fieldValue || 0) + step)
    setValue(name, `${ nextValue }`, { shouldDirty: true, shouldValidate: true })
  }, [getValues, max, name, setValue, step])

  const handleDecrement = useCallback(() => {
    const fieldValue = parseFloat(getValues(name))
    const nextValue = Math.max(min, (fieldValue || 0) - step)
    setValue(name, `${ nextValue }`, { shouldDirty: true, shouldValidate: true })
  }, [getValues, min, name, setValue, step])

  const controlChild = useMemo(() => (
    <FutureFieldControlButtons
      isDisabled={ isPremiumRequired || !!isDisabled }
      onDecrement={ handleDecrement }
      onIncrement={ handleIncrement }
    />
  ), [handleDecrement, handleIncrement, isPremiumRequired, isDisabled])

  const value = watch(name) as string

  const inputChild = useMemo(() => isPremiumRequired ? (
    <PremiumInput name={ name } value={ value }>
      { IMask.pipe(`${ value }`, mask as IMask.AnyMask) }
    </PremiumInput>
  ) : (
    <div style={{ width: max <= 100 && !units ? '80px' : '128px' }}>
      <MaskedInputController options={ mask } name={ name } renderer={ FutureInput } inputMode={ inputMode } onFocus={ onFocus } />
    </div>
  ), [isPremiumRequired, name, value, mask, max, units, inputMode, onFocus])

  return (
    <ThemeProvider inputDisabled={ isDisabled }>
      <FutureInputDecoration
        label={ label }
        error={ error }
        note={ note }
        units={ units }
      >
        <FutureInputWithControl control={ controlChild }>
          { inputChild }
        </FutureInputWithControl>
      </FutureInputDecoration>
    </ThemeProvider>
  )
}
